import { useState, type FC } from "react"
import { useNavigate } from "react-router-dom"

import { useMutation, useQuery } from "@apollo/client"

import styles from "./UnknownIssueLogsPage.module.scss"

import {
	LogLevelBadge,
	ModuleBadge,
	OpenSearchLink,
} from "@/souhl_admin/components/common_features/Badges"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import type { LogLevel } from "@/souhl_admin/gql/__generated__/graphql"
import { logModuleNameValues } from "@/souhl_admin/utils/forms/knownIssue"

// TODO: ほぼみてない

const UnknownIssueLogsQuery = graphql(/* GraphQL */ `
	query UnknownIssueLogs($param: UnknownIssueLogsParam) {
		unknownIssueLogs(param: $param) {
			totalCount
			logs {
				time
				reqId
				level
				message
				moduleName
				kind
				salonId
				opensearchUrl
			}
		}
	}
`)

const HideUnknownIssueLogsMutation = graphql(/* GraphQL */ `
	mutation HideUnknownIssueLogs($input: HideUnknownIssueLogsInput!) {
		hideUnknownIssueLogs(input: $input) {
			hiddenCount
		}
	}
`)

type FilterState = {
	logLevel: LogLevel | null
	moduleName: string
	kind: string
	salonId: string
	limit: number
}

const UnknownIssueLogsPage: FC = () => {
	const navigate = useNavigate()
	const [filter, setFilter] = useState<FilterState>({
		logLevel: null,
		moduleName: "",
		kind: "",
		salonId: "",
		limit: 200,
	})
	const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null)
	const [tooltipPosition, setTooltipPosition] = useState<{
		x: number
		y: number
	} | null>(null)
	const [selectedReqIds, setSelectedReqIds] = useState<Set<string>>(new Set())

	const [hideUnknownIssueLogs, { loading: hideLoading }] = useMutation(
		HideUnknownIssueLogsMutation
	)

	const { data, loading, error, refetch } = useQuery(UnknownIssueLogsQuery, {
		variables: {
			param: {
				logLevel: filter.logLevel,
				moduleName: filter.moduleName || null,
				kind: filter.kind || null,
				salonId: filter.salonId ? parseInt(filter.salonId, 10) : null,
				limit: filter.limit,
			},
		},
	})

	const handleCreateKnownIssue = (log: {
		level: string
		message: string
		moduleName: string
		kind?: string | null
		salonId?: number | null
	}) => {
		const prefillData = {
			logLevel: log.level,
			logMatchMethod: "PARTIAL",
			logKeyword: log.message.slice(0, 100),
			logModuleName: log.moduleName,
			logKind: log.kind || "",
			logSalonIds: log.salonId?.toString() || "",
		}
		// btoa doesn't support Unicode, so we need to encode to UTF-8 first
		const encoded = btoa(
			unescape(encodeURIComponent(JSON.stringify(prefillData)))
		)
		navigate(`/souhl_admin/known_issues/create?prefill=${encoded}`)
	}

	const handleSelectAll = () => {
		if (!data) return
		const allReqIds = data.unknownIssueLogs.logs.map((log) => log.reqId)
		if (selectedReqIds.size === allReqIds.length) {
			setSelectedReqIds(new Set())
		} else {
			setSelectedReqIds(new Set(allReqIds))
		}
	}

	const handleToggleSelect = (reqId: string) => {
		const newSelected = new Set(selectedReqIds)
		if (newSelected.has(reqId)) {
			newSelected.delete(reqId)
		} else {
			newSelected.add(reqId)
		}
		setSelectedReqIds(newSelected)
	}

	const handleHide = async () => {
		if (selectedReqIds.size === 0) return
		try {
			await hideUnknownIssueLogs({
				variables: {
					input: {
						reqIds: Array.from(selectedReqIds),
					},
				},
			})
			setSelectedReqIds(new Set())
			void refetch()
		} catch (e) {
			console.error("Failed to hide unknown issue logs:", e)
		}
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>Unknown Issue Logs</h1>
				<p className={styles.subtitle}>
					直近48時間で KnownIssue にマッチしなかったログ一覧
				</p>
			</div>

			{/* Filter */}
			<div className={styles.filterPanel}>
				<div className={styles.filterRow}>
					<div className={styles.filterItem}>
						<label>Log Level</label>
						<select
							value={filter.logLevel || ""}
							onChange={(e) =>
								setFilter({
									...filter,
									logLevel: (e.target.value as LogLevel) || null,
								})
							}
						>
							<option value="">All</option>
							<option value="CRITICAL">CRITICAL</option>
							<option value="ERROR">ERROR</option>
							<option value="WARNING">WARNING</option>
						</select>
					</div>
					<div className={styles.filterItem}>
						<label>Module</label>
						<select
							value={filter.moduleName}
							onChange={(e) =>
								setFilter({ ...filter, moduleName: e.target.value })
							}
						>
							<option value="">All</option>
							{logModuleNameValues.map((value) => (
								<option key={value} value={value}>
									{value}
								</option>
							))}
						</select>
					</div>
					<div className={styles.filterItem}>
						<label>Kind</label>
						<input
							type="text"
							placeholder="NotFoundException"
							value={filter.kind}
							onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
						/>
					</div>
					<div className={styles.filterItem}>
						<label>Salon ID</label>
						<input
							type="text"
							placeholder="123"
							value={filter.salonId}
							onChange={(e) =>
								setFilter({ ...filter, salonId: e.target.value })
							}
						/>
					</div>
					<div className={styles.filterItem}>
						<label>Limit</label>
						<select
							value={filter.limit}
							onChange={(e) =>
								setFilter({ ...filter, limit: parseInt(e.target.value, 10) })
							}
						>
							<option value="100">100</option>
							<option value="200">200</option>
							<option value="500">500</option>
						</select>
					</div>
					<button
						className={styles.refreshButton}
						onClick={() => {
							void refetch()
						}}
					>
						Refresh
					</button>
					<button
						className={styles.hideButton}
						disabled={selectedReqIds.size === 0 || hideLoading}
						onClick={() => {
							void handleHide()
						}}
					>
						Hide ({selectedReqIds.size})
					</button>
				</div>
			</div>

			{/* Count */}
			{data && (
				<p className={styles.countText}>
					全 {data.unknownIssueLogs.totalCount} 件中{" "}
					{data.unknownIssueLogs.logs.length} 件表示
				</p>
			)}

			{/* Table */}
			<div className={styles.tableWrapper}>
				{loading ? (
					<p className={styles.loading}>Loading...</p>
				) : error ? (
					<p className={styles.error}>Error: {error.message}</p>
				) : (
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.headerCell}>
									<input
										type="checkbox"
										checked={
											data !== undefined &&
											data.unknownIssueLogs.logs.length > 0 &&
											selectedReqIds.size === data.unknownIssueLogs.logs.length
										}
										onChange={handleSelectAll}
									/>
								</th>
								<th className={styles.headerCell}>時刻</th>
								<th className={styles.headerCell}>Level</th>
								<th className={styles.headerCell}>Module</th>
								<th className={styles.headerCell}>Kind</th>
								<th className={styles.headerCell}>Salon</th>
								<th className={styles.headerCell}>Message</th>
								<th className={styles.headerCell}>ReqID</th>
								<th className={styles.headerCell}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{data?.unknownIssueLogs.logs.length === 0 ? (
								<tr>
									<td colSpan={9} className={styles.emptyCell}>
										データがありません
									</td>
								</tr>
							) : (
								data?.unknownIssueLogs.logs.map((log) => (
									<tr key={`${log.reqId}-${log.time}`} className={styles.row}>
										<td className={styles.cell}>
											<input
												type="checkbox"
												checked={selectedReqIds.has(log.reqId)}
												onChange={() => handleToggleSelect(log.reqId)}
											/>
										</td>
										<td className={styles.cell}>
											<span className={styles.timeCell}>
												{new Date(log.time).toLocaleString("ja-JP")}
											</span>
										</td>
										<td className={styles.cell}>
											<LogLevelBadge level={log.level} />
										</td>
										<td className={styles.cell}>
											<ModuleBadge moduleName={log.moduleName} />
										</td>
										<td className={styles.cell}>
											<span className={styles.kindCell}>{log.kind || "-"}</span>
										</td>
										<td className={styles.cell}>
											<span className={styles.salonCell}>
												{log.salonId ?? "-"}
											</span>
										</td>
										<td className={styles.cell}>
											<div
												className={styles.messageWrapper}
												onMouseEnter={(e) => {
													setHoveredMessageId(`${log.reqId}-${log.time}`)
													const rect = e.currentTarget.getBoundingClientRect()
													setTooltipPosition({
														x: rect.left,
														y: rect.bottom + 4,
													})
												}}
												onMouseLeave={() => {
													setHoveredMessageId(null)
													setTooltipPosition(null)
												}}
											>
												<span className={styles.messageCell}>
													{log.message.length > 100
														? `${log.message.slice(0, 100)}...`
														: log.message}
												</span>
												{log.message.length > 100 &&
													hoveredMessageId === `${log.reqId}-${log.time}` &&
													tooltipPosition && (
														<div
															className={styles.messageTooltip}
															style={{
																position: "fixed",
																left: tooltipPosition.x,
																top: tooltipPosition.y,
															}}
														>
															{log.message}
														</div>
													)}
											</div>
										</td>
										<td className={styles.cell}>
											<div className={styles.reqIdWrapper}>
												<code className={styles.reqIdCell}>{log.reqId}</code>
												<button
													className={styles.copyButton}
													onClick={() => {
														void navigator.clipboard.writeText(log.reqId)
													}}
													title="Copy ReqID"
												>
													📋
												</button>
											</div>
										</td>
										<td className={styles.cell}>
											<div className={styles.actionLinks}>
												<OpenSearchLink url={log.opensearchUrl} />
												<button
													className={styles.createButton}
													onClick={() => handleCreateKnownIssue(log)}
												>
													Create KI
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				)}
			</div>
		</div>
	)
}

export default UnknownIssueLogsPage
